import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Nav, Navbar as RbNavbar, NavbarBrand } from 'react-bootstrap';
import CreateSearchButton from '../CreateSearchButton/CreateSearchButton';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { FEATURE_FLAGS } from 'utils/constants';
import { GenericObject, Role, User, Permission } from 'v2/models';

import { getUserRole, isRequestViewerOnly } from 'v2/utils/user';
import nomadLogo from '../../images/nomadLogo.svg';
import styles from './navbar.module.scss';
import magicWand from 'v2/icons/magicWand.svg';
import { hasPermission, PERMISSION_NAME } from '../../utils/permissions';
import { BsXLg } from 'react-icons/bs';
import SubscribeButton from 'v2/common/SubscribeButton/SubscribeButton';
import UpgradeButton from 'v2/common/Navbar/UpgradeButton';
import NavbarLinkItem from './NavbarLinkItems/NavbarLinkItem';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { SettingsDropdown } from './SettingsDropdown/SettingsDropdown';
import { MobileSettingsItems } from './MobileSettingsItems';

export type NavItem = {
  to: string;
  label: string;
  key: string;
  featureFlag?: string;
  getCounter?: (
    data: Array<number | string> | { [key: string]: string | number }
  ) => number;
  permissions?: string[];
  icon?: string;
  title?: boolean;
  line?: boolean;
  hideMobile?: boolean;
  hideDesktop?: boolean;
};

export const getNavItemsBuyer = (isFreeConnectEnabled: boolean): NavItem[] => [
  {
    to: '/requests',
    label: 'Requests',
    key: 'requests',
    permissions: [
      PERMISSION_NAME.CREATE_SEARCH,
      PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
      PERMISSION_NAME.CREATE_SEARCH_OWN,
      PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
      PERMISSION_NAME.REVIEW_SEARCH_ORG,
      PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
      PERMISSION_NAME.MONITOR_SEARCHES_MASTER_ORG,
      PERMISSION_NAME.MONITOR_SEARCHES_ORG,
      ...(isFreeConnectEnabled ? [PERMISSION_NAME.CREATE_SEARCH_FREE] : []),
    ],
    line: true,
    title: true,
  },
  {
    to: '/interactions/1',
    label: 'INTERACTIONS',
    key: 'interactions',
    permissions: [PERMISSION_NAME.DRM],
    line: true,
    title: true,
  },
  {
    to: '/file-library',
    label: 'File Library',
    key: 'file-library',
    featureFlag: FEATURE_FLAGS.DATA_BUYER_FILE_LIBRARY,
    permissions: [
      PERMISSION_NAME.CREATE_SEARCH,
      PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
      PERMISSION_NAME.CREATE_SEARCH_OWN,
      PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
    ],
    line: true,
    title: true,
  },
  {
    to: '/ai-search',
    label: 'Search',
    key: 'ai-search',
    featureFlag: FEATURE_FLAGS.AI_THREAD,
    permissions: [PERMISSION_NAME.DOCUMENT_CHAT, PERMISSION_NAME.DRM_CHAT],
    icon: magicWand,
    line: true,
    title: true,
  },
];

export const NAV_ITEMS_PROVIDER: NavItem[] = [
  {
    to: '/data-requests/active/waiting-seller',
    label: 'Data requests',
    key: 'data-requests',
    title: true,
  },
  {
    to: '/data-requests/active/waiting-seller',
    label: 'Active Request',
    key: 'data-active-requests',
    title: false,
    hideDesktop: true,
  },
  {
    to: '/data-requests/closed/approved',
    label: 'Closed Request',
    key: 'data-closed-requests',
    title: false,
    hideDesktop: true,
  },
  {
    to: '/data-requests/notifications',
    label: 'Notifications',
    key: 'data-notifications',
    title: false,
    line: true,
    hideDesktop: true,
  },
  {
    to: '/discussions',
    label: 'Discussions',
    key: 'discussions',
    getCounter: (unreadMessages) => Object.keys(unreadMessages).length,
    title: true,
    line: true,
  },
  {
    to: '/file-library',
    label: 'File Library',
    key: 'file-library',
    featureFlag: FEATURE_FLAGS.DATA_FILE,
    title: true,
    hideMobile: true,
  },
];

export const getNavItemsNomadAdmin: (ghosting_admin: boolean) => NavItem[] = (
  ghosting_admin
) => {
  const items = [
    {
      to: '/top-providers',
      label: 'Top Providers',
      key: 'top-providers',
      line: true,
      title: true,
    },
    {
      to: '/proposal-token',
      label: 'Proposal token',
      key: 'proposal-token',
      line: true,
      title: true,
    },
  ];
  if (ghosting_admin) {
    items.unshift({
      to: '/all-users',
      label: 'Users',
      key: 'all-users',
      line: true,
      title: true,
    });
    items.push({
      to: '/accept-proposal',
      label: 'ACCEPT PROPOSALS',
      key: 'accept-proposal',
      line: true,
      title: true,
    });
  }
  return items;
};

interface StateProps {
  user: User;
  permissions: { [key: string]: Permission };
}

const Navbar = ({ user, permissions }: StateProps) => {
  const location = useLocation();
  const userRole: Role = getUserRole(user);
  const showSettings = userRole !== 'nomadAdmin';
  const [expanded, setExpanded] = useState(false);

  const hasDrmPermission = hasPermission(permissions, [PERMISSION_NAME.DRM]);
  const { flags } = useFeatureFlagContext();

  const getFilteredNavlinks = () => {
    const isFreeConnectEnabled = flags && flags[FEATURE_FLAGS.FREE_CONNECT];
    if (user && user.active) {
      const isReviewer = hasPermission(permissions, [
        PERMISSION_NAME.REVIEW_SEARCH_ORG,
        PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
      ]);

      let navItems: NavItem[] = [];
      switch (userRole) {
        case 'dataProvider':
          navItems = NAV_ITEMS_PROVIDER;
          break;
        case 'dataBuyer':
          navItems = getNavItemsBuyer(isFreeConnectEnabled);
          if (isReviewer) {
            navItems[0].to = '/requests';
          }
          break;
        case 'nomadAdmin':
          navItems = getNavItemsNomadAdmin(user.ghosting_admin);
          break;
      }
      return navItems;
    }
    return [];
  };

  useEffect(() => {
    getFilteredNavlinks();
  }, [expanded]);

  const shouldRenderCreateSearchBtn =
    !isRequestViewerOnly(permissions) &&
    userRole === 'dataBuyer' &&
    location.pathname !== '/create' &&
    !/^\/edit\/[0-9]+$/.test(location.pathname);

  const renderCreateSearchBtn = () => {
    if (shouldRenderCreateSearchBtn) {
      return <CreateSearchButton />;
    }
    return null;
  };

  const renderNavbarBrand = () => {
    if (expanded) {
      return null;
    }
    return (
      <NavbarBrand
        to={
          user?.new_user || (user?.waitlisted && !hasDrmPermission) ? '#' : '/'
        }
        className="mr-5 pr-5"
        as={Link}
      >
        <img src={nomadLogo} alt="logo"></img>
      </NavbarBrand>
    );
  };

  return (
    <RbNavbar
      className={classnames(styles.root, {
        [styles.customNavBar]: expanded,
      })}
      expand="lg"
      id="topNavBar"
      expanded={expanded}
    >
      <div
        className={classnames('container-xl', {
          [styles.togglerContainer]: expanded,
        })}
      >
        {renderNavbarBrand()}
        {!user?.new_user &&
        (!user?.waitlisted || (user?.waitlisted && hasDrmPermission)) &&
        user.active ? (
          <>
            <RbNavbar.Toggle
              aria-controls="navbarScroll"
              onClick={() => setExpanded(!expanded)}
              className={classnames(styles.navbarToggler)}
            >
              {expanded ? <BsXLg color="white" size={30} /> : null}
            </RbNavbar.Toggle>
            <RbNavbar.Collapse id="navbarScroll">
              <Nav className="mr-auto my-lg-2 mt-3 my-lg-0 align-items-lg-center">
                {getFilteredNavlinks().map((item, index) => (
                  <NavbarLinkItem
                    key={index}
                    item={item}
                    handleExpand={setExpanded}
                  />
                ))}
              </Nav>
              <div
                className={classnames(
                  'd-flex flex-column flex-lg-row align-items-lg-center',
                  styles.rightItems
                )}
                data-testid="rightItems"
              >
                <MobileSettingsItems
                  show={expanded}
                  showSettings={showSettings}
                  setExpanded={setExpanded}
                />
                <Nav>
                  <SubscribeButton expanded={expanded} />
                </Nav>
                <Nav onClick={() => setExpanded(false)}>
                  {renderCreateSearchBtn()}
                </Nav>
                <Nav>
                  <UpgradeButton />
                </Nav>
                <SettingsDropdown
                  show={!expanded}
                  showSettings={showSettings}
                  handleExpand={setExpanded}
                />
              </div>
            </RbNavbar.Collapse>
          </>
        ) : null}
      </div>
    </RbNavbar>
  );
};

const mapStateToProps = (state: GenericObject) => {
  const user = state.user?.user || {};
  return {
    user: user,
    permissions: state.permissions.permissions,
  };
};

export default connect<StateProps>(mapStateToProps)(Navbar);
